import clsx from 'clsx'
import { useRef, useState } from 'react'
import { ChevronDown, Columns3, Download, RefreshCw } from 'lucide-react'
import { FilterChips, SearchBox } from '../cc'
import { findCatalogItem } from './reportCatalog'
import { reportMenuPanel } from './ui'

interface ReportToolbarProps {
  reportId: string
  rowCount: number
  selectedCount: number
  filter: string
  onFilterChange: (v: string) => void
  dayOptions: string[]
  dayFilter: string
  onDayFilterChange: (v: string) => void
  allColumns: string[]
  visibleColumns: Set<string>
  onToggleColumn: (col: string) => void
  onRefresh: () => void
  onExport: (limit: number) => void
  fetchedAt: Date | null
  loading?: boolean
  hideTitle?: boolean
}

const btnClass =
  'flex h-9 items-center gap-1.5 rounded-md bg-white px-3 text-sm font-medium text-slate-700 ' +
  'shadow-sm ring-1 ring-inset ring-slate-300 transition-[background-color,color,transform] ' +
  'duration-100 hover:bg-slate-50 hover:text-slate-900 active:scale-[0.97] ' +
  'disabled:pointer-events-none disabled:opacity-50'

export function ReportToolbar({
  reportId, rowCount, selectedCount, filter, onFilterChange,
  dayOptions, dayFilter, onDayFilterChange,
  allColumns, visibleColumns, onToggleColumn,
  onRefresh, onExport, fetchedAt, loading, hideTitle,
}: ReportToolbarProps) {
  const catalog = findCatalogItem(reportId)
  const [colsOpen, setColsOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const colsRef = useRef<HTMLDivElement>(null)

  const windowBadge = catalog?.windowLabel ? (
    <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 font-mono text-xs uppercase tracking-wide text-slate-600 ring-1 ring-inset ring-slate-200">
      Window: {catalog.windowLabel}
    </span>
  ) : null

  const fetchedBadge = fetchedAt ? (
    <span className="font-mono text-xs tabular-nums text-slate-400">
      fetched {fetchedAt.toLocaleTimeString()}
    </span>
  ) : null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {hideTitle ? (
          <div className="flex flex-wrap items-center gap-2">
            {windowBadge}
            {fetchedBadge}
          </div>
        ) : (
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">
              {catalog?.label ?? reportId}
            </h2>
            {catalog?.description ? (
              <p className="mt-0.5 text-sm text-slate-500">{catalog.description}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {windowBadge}
              {fetchedBadge}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className={btnClass}
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportOpen((o) => !o)}
              aria-expanded={exportOpen}
              className={btnClass}
            >
              <Download className="h-3.5 w-3.5" />
              Export
              <ChevronDown
                className={clsx('h-3 w-3 text-slate-400 transition-transform duration-150', exportOpen && 'rotate-180')}
              />
            </button>
            {exportOpen ? (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className={clsx(reportMenuPanel, 'w-48')}>
                  <button
                    type="button"
                    onClick={() => { onExport(500); setExportOpen(false) }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-75 hover:bg-slate-50"
                  >
                    CSV · current view (500)
                  </button>
                  <button
                    type="button"
                    onClick={() => { onExport(5000); setExportOpen(false) }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-75 hover:bg-slate-50"
                  >
                    CSV · all rows (5000 max)
                  </button>
                  <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-400">
                    Server cap: 5000 rows
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[200px] flex-1">
          <SearchBox
            value={filter}
            onChange={onFilterChange}
            placeholder="Filter loaded rows…"
            counter={selectedCount > 0 ? `${selectedCount} sel` : `${rowCount}`}
          />
        </div>
        {dayOptions.length > 0 ? (
          <FilterChips
            options={[
              { id: 'all', label: 'All days' },
              ...dayOptions.map((d) => ({ id: d, label: d })),
            ]}
            active={dayFilter}
            onPick={onDayFilterChange}
          />
        ) : null}
        <div className="relative" ref={colsRef}>
          <button
            type="button"
            onClick={() => setColsOpen((o) => !o)}
            aria-expanded={colsOpen}
            className={btnClass}
          >
            <Columns3 className="h-3.5 w-3.5" />
            Columns
          </button>
          {colsOpen ? (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setColsOpen(false)} />
              <div className={clsx(reportMenuPanel, 'max-h-64 w-52 overflow-auto')}>
                {allColumns.map((col) => (
                  <label
                    key={col}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 transition-colors duration-75 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(col)}
                      onChange={() => onToggleColumn(col)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                    />
                    {col.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
